import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { datetime, expr, map, maybe, string } from '@codegrade/utils';
import Error from '@/components/Error';
import Loader from '@/components/Loader';
import RelativeTime from '@/components/RelativeTime';
import { AssignmentsStore, SectionsStore, SiteSettingsStore } from '@/store';
import * as utils from '@/utils';
import { emptyVNode, getUniqueId, ifJustOrEmpty, readableJoin } from '@/utils';
import './style.less';
/**
 * Renders the lockdate if present with different options.
 *
 * @param h Vue's hyperscript function. Unused.
 * @param vue Vue's root instance.
 * @param lockDate A lockdate.
 * @param relative Whether to render the lockdate as relative or absolute time.
 * @returns The rendered lockdate caption if lockdate was set. An empty node otherwise.
 */
function renderLockDate(h, vue, lockDate, relative) {
    const locks = lockDate.isAfter(vue.$getNow(lockDate)) ? 'locks' : 'locked';
    const content = expr.ifExpr(relative, () => (<span class="text-nowrap">
                {`and ${locks} `}
                <RelativeTime date={lockDate}/>
            </span>), () => (<span class="text-nowrap">{`and ${locks} at ${datetime.readableFormatDate(lockDate)}`}</span>));
    return [', ', content];
}
/**
 * Renders a timed availability for an assignment.
 *
 * @param h Vue's hyperscript function. Unused.
 * @param availableAt The moment object of a timed assignment availability.
 * @param noCapital Whether to capitalize the first letter of the resulting sentence.
 * @returns The rendered timed availability of the assignment.
 */
function renderAvailableAt(h, availableAt, noCapital) {
    const starts = noCapital ? 'starts' : 'Starts';
    const content = [starts, ' ', <RelativeTime date={availableAt}/>];
    return <span class="text-nowrap">{content}</span>;
}
/**
 * Renders the deadline, with different options.
 * If a lockdate is also passed both of them are chained in a single sentence.
 *
 * @param h Vue's hyperscript function.
 * @param vue The Vue root instance.
 * @param deadline The deadline moment object.
 * @param maybeLockDate A lockdate moment object if set.
 * @param relative Whether to show in relative or absolute time.
 * @param noCapital Whether to uncapitalize the starting word.
 * @returns The rendered deadline and lockdate if the latter is present.
 */
function renderDeadline(h, vue, deadline, maybeLockDate, relative, noCapital) {
    const due = noCapital ? 'due' : 'Due';
    const res = (<span class="text-nowrap">
            {expr.ifExpr(relative, () => [`${due} `, <RelativeTime date={deadline}/>], () => `${due} at ${datetime.readableFormatDate(deadline)}`)}
        </span>);
    return [
        res,
        ifJustOrEmpty(maybeLockDate, lockDate => renderLockDate(h, vue, lockDate, relative)),
    ];
}
const LoadedMultipleAssignmentDatesPopover = tsx.component({
    props: {
        timeframe: p(api.AssignmentTimeframes).required,
        sections: p.ofRoArray().required,
    },
    methods: {
        /**
         *
         * @param maybeDate The date referred to the current object if set.
         * @param what Which datetime object is being rendered. Used for the non-set caption.
         * @returns The rendered datetime in humanized relative time.
         */
        renderMaybeDate(maybeDate, what) {
            return maybeDate.caseOf({
                Just: date => (<RelativeTime date={date} transformText={string.capitalize} argThreshold={{ ss: -1 /* Never display "just now" */, s: 30 }}/>),
                Nothing: () => <span class="text-muted">No {what} set</span>,
            });
        },
        /**
         * Renders the Availability caption for an assignment.
         *
         * @param timeframe The timeframe of the assignment.
         * @returns The rendered availability, wether as an adverb in case of fixed availability,
         * or in humanized relative time if the assignment is set to be available at a specific date.
         */
        renderAvailability(timeframe) {
            return expr.match(timeframe.availability, 'tag', {
                fixed: ({ value }) => (value ? 'Always' : 'Never'),
                timed: ({ availableAt }) => (<RelativeTime date={availableAt} transformText={string.capitalize} argThreshold={{ ss: -1 /* Never display "just now" */, s: 30 }}/>),
            });
        },
        /**
         *
         * @param title Renders the title of the timeframe.
         * @param timeframe The timeframe object for the section.
         * @param key A key to be given to the table row.
         * @returns A table row with title, availability, deadline and lockdate.
         */
        renderTimeframeLike(title, timeframe, key) {
            return (<tr key={key}>
                    <td>{title}</td>
                    <td>{this.renderAvailability(timeframe)}</td>
                    <td>{this.renderMaybeDate(timeframe.deadline, 'deadline')}</td>
                    <td>{this.renderMaybeDate(timeframe.lockDate, 'lock date')}</td>
                </tr>);
        },
    },
    computed: {
        /** @returns Section lookup from id. */
        sectionLookup() {
            return new map.ReadonlyMaybeMap(this.sections.map(section => [section.id, section]));
        },
        /** @returns The table header for the timeframes popover. */
        tableHeaderNode() {
            return (<thead>
                    <tr>
                        <th class="border-top-0">Who</th>
                        <th class="text-nowrap border-top-0">Availability</th>
                        <th class="text-nowrap border-top-0">Deadline</th>
                        <th class="text-nowrap border-top-0">Lock date</th>
                    </tr>
                </thead>);
        },
        /** @returns The general timeframe table row.  */
        generalTimeframeNode() {
            return this.renderTimeframeLike('General', this.timeframe.general);
        },
    },
    /** @returns The rendered table of timeframes. */
    render() {
        const sectionLookup = this.sectionLookup;
        return (<table class="table mb-0 table-sm text-left">
                {this.tableHeaderNode}
                <tbody>
                    {this.generalTimeframeNode}
                    {this.timeframe.sections.map(sectionTimeframe => this.renderTimeframeLike(readableJoin(sectionTimeframe.sectionIds.map(sectionId => sectionLookup
                .get(sectionId)
                .mapOrDefault(section => section.name, 'Unknown'))), sectionTimeframe, sectionTimeframe.sectionIds.join('-')))}
                </tbody>
            </table>);
    },
});
const MultipleAssignmentDatesPopover = tsx
    .componentFactoryOf()
    .create({
    props: {
        assignment: p.ofType().required,
    },
    /** @returns The stateful data for the popover content.  */
    data() {
        return {
            error: Maybe.empty(),
        };
    },
    computed: {
        /** @returns The assignment timeframe from the store. */
        timeframe() {
            return AssignmentsStore.getTimeframe()(this.assignment.id);
        },
        /** @returns The sections for this assignment retrieved from the store. */
        sections() {
            return SectionsStore.getAllCourseSections()(this.assignment.courseId);
        },
    },
    /** Called when the component is created. */
    created() {
        utils.watchedLoad({
            instance: this,
            /** @returns The values to watch for. */
            getValues() {
                return { assignmentId: this.assignment.id, courseId: this.assignment.courseId };
            },
            load: async ({ assignmentId, courseId }) => {
                await Promise.all([
                    AssignmentsStore.loadAssignmentTimeframe({ assignmentId }),
                    SectionsStore.loadCourseSections({ courseId }),
                ]);
            },
            /**
             * Set the error correctly.
             *
             * @param error The error that occured.
             */
            setError(error) {
                this.error = Maybe.of(error);
            },
            /** Emit that we loaded the data. */
            setResult() {
                tsx.emitOn(this, 'onLoaded');
            },
            /** Reset the state back to the loading state. */
            reset() {
                this.error = Maybe.empty();
            },
        });
    },
    /** @returns The rendered timeframe for the assignment. */
    render() {
        return maybe.and(this.timeframe, this.sections).caseOf({
            Just: ([timeframe, sections]) => (<LoadedMultipleAssignmentDatesPopover timeframe={timeframe} sections={sections}/>),
            Nothing: () => this.error.caseOf({
                Just: err => <Error error={err.error} doRetry={err.retry}/>,
                Nothing: () => <Loader scale={1}/>,
            }),
        });
    },
});
const MultipleAssignmentDates = tsx.component({
    props: {
        assignment: p.ofType().required,
    },
    /** @returns Stateful data for the Multiple assignment date component. */
    data() {
        return {
            linkId: `assignment-multiple-timeframes-${getUniqueId()}`,
            popoverOffset: 0,
            popoverOpen: false,
        };
    },
    /**
     * @returns The `Multiple timeframes` caption, with a popover on hover that
     * describes each timeframe.
     */
    render() {
        return (<span>
                <span style={{
                textDecorationStyle: 'dashed',
                textDecorationLine: 'underline',
                cursor: 'help',
            }} title="Click for more information" id={this.linkId} onClick={tsx.modifiers.stop.prevent(() => {
                this.popoverOpen = !this.popoverOpen;
            })}>
                    Multiple schedules
                </span>
                <b-popover v-model={this.popoverOpen} custom-class="deadline-popover" target={this.linkId} placement="bottom" triggers="click blur" boundary="window" offset={this.popoverOffset}>
                    <div tabindex={-1} style={{ outline: 'none' }} ref="popoverBodyWrapper">
                        <MultipleAssignmentDatesPopover assignment={this.assignment} onLoaded={() => {
                this.popoverOffset = this.popoverOffset === 0 ? 0.1 : 0;
                this.$withRef('popoverBodyWrapper', el => {
                    el.focus();
                });
            }}/>
                    </div>
                </b-popover>
            </span>);
    },
});
const AssignmentDate = tsx.component({
    props: {
        assignment: p.ofType().required,
        relative: p(Boolean).default(false),
        noCapital: p(Boolean).default(false),
        withoutLockDate: p(Boolean).default(false),
        wrapperTag: p(String).default('small'),
        tenantId: p.ofType().required,
    },
    computed: {
        /** @returns The content of the component. */
        content() {
            /** @returns The rendered MultipleAssignmentDates component for this assignment.  */
            const renderMultiple = () => [
                <MultipleAssignmentDates assignment={this.assignment}/>,
            ];
            return expr.ifExpr(this.assignment.hasMultipleTimeframes, renderMultiple, this.renderSingle);
        },
    },
    methods: {
        /** @returns A node for assignments that are not not started exams. */
        renderOther() {
            const maybeLockDate = this.assignment.lockDate.filter(() => !this.withoutLockDate);
            return this.assignment.deadline.caseOf({
                Just: deadline => renderDeadline(this.$createElement, this.$getRootInstance(), deadline, maybeLockDate, this.relative, this.noCapital),
                Nothing: () => {
                    const maybeLockDateText = maybeLockDate.map(lockDate => renderLockDate(this.$createElement, this.$getRootInstance(), lockDate, this.relative));
                    return maybeLockDateText.caseOf({
                        Just: lockDateText => <i class="text-muted">No deadline{lockDateText}</i>,
                        Nothing: () => {
                            if (SiteSettingsStore.getSetting()('HIDE_NO_DEADLINE_ENABLED', this.tenantId)) {
                                return emptyVNode();
                            }
                            return <i class="text-muted">No deadline</i>;
                        },
                    });
                },
            });
        },
        /** @returns The rendered assignment timeframe, in case of a single one being present. */
        renderSingle() {
            return expr.match(this.assignment.availability, 'tag', {
                fixed: this.renderOther,
                timed: ({ availableAt }) => {
                    if (this.assignment.kind === 'exam' &&
                        availableAt.isAfter(this.$getRootInstance().$getNow(availableAt))) {
                        return renderAvailableAt(this.$createElement, availableAt, this.noCapital);
                    }
                    return this.renderOther();
                },
            });
        },
    },
    /** @returns The computed VNode for the component. */
    render() {
        return this.$createElement(this.wrapperTag, { class: 'assignment-date' }, [this.content]);
    },
});
export default AssignmentDate;
